import { useState, useMemo } from "react";
import axios from "axios";
import { format } from "date-fns";
import { Mail, CheckCircle2, Globe, AlertTriangle, XCircle, PhoneCall } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { API, formatApiError } from "@/lib/shared";

export const WebsiteBookingsBanner = ({ appointments, patients, serviceTypes, staff = [], onRefresh, onJumpToDate }) => {
  const pending = useMemo(() => {
    return (appointments || [])
      .filter(a => a && a.source === 'website' && !a.confirmed && a.status !== 'cancelled' && a.status !== 'completed')
      .sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime));
  }, [appointments]);

  const [expanded, setExpanded] = useState(false);
  const [busyId, setBusyId] = useState(null);
  // Per-booking nurse selection (map of appointment_id → staff_id)
  const [assignments, setAssignments] = useState({});
  // Active nurses/staff to choose from
  const activeStaff = useMemo(
    () => (staff || []).filter(s => s.is_active !== false),
    [staff]
  );

  if (pending.length === 0) return null;

  const confirmOne = async (apt) => {
    try {
      setBusyId(apt.id);
      const chosenStaff = assignments[apt.id];
      const previousStaff = Array.isArray(apt.staff_ids) ? apt.staff_ids : [];
      // If a nurse was selected, save the assignment BEFORE confirming.
      if (chosenStaff) {
        await axios.put(`${API}/appointments/${apt.id}`, { staff_ids: [chosenStaff] });
      }
      try {
        await axios.post(`${API}/appointments/${apt.id}/confirm`);
      } catch (confirmErr) {
        // Roll back the staff assignment so we don't leave a silently-assigned but unconfirmed appointment.
        if (chosenStaff) {
          try { await axios.put(`${API}/appointments/${apt.id}`, { staff_ids: previousStaff }); } catch (_) { /* best-effort */ }
        }
        throw confirmErr;
      }
      toast.success(chosenStaff ? 'Booking confirmed & nurse assigned' : 'Booking confirmed');
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Could not confirm booking');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      className="mb-6 rounded-xl border border-amber-400/40 bg-gradient-to-r from-amber-500/10 via-amber-400/[0.06] to-transparent p-4 sm:p-5 shadow-[0_0_30px_rgba(212,175,55,0.08)]"
      data-testid="website-bookings-banner"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 shrink-0 rounded-full bg-amber-400/15 p-2 ring-1 ring-amber-400/40">
          <Globe className="w-4 h-4 text-amber-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.22em] uppercase text-amber-300">
              <AlertTriangle className="w-3 h-3" /> New Website Bookings
            </span>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-400/15 text-amber-200 text-[11px] font-semibold ring-1 ring-amber-400/30" data-testid="website-bookings-count">
              {pending.length} pending
            </span>
          </div>
          <p className="mt-1 text-sm text-amber-100/90">
            {pending.length === 1 ? 'A patient just booked through the website.' : `${pending.length} patients just booked through the website.`} Assign a nurse (optional) and confirm.
          </p>

          <div className="mt-3 space-y-2">
            {(expanded ? pending : pending.slice(0, 3)).map(apt => {
              const p = patients.find(x => x.id === apt.patient_id);
              const svc = serviceTypes.find(x => x.id === apt.service_type_id);
              const when = apt.start_datetime ? new Date(apt.start_datetime) : null;
              const whenLabel = when ? `${format(when, 'EEE, MMM d')} · ${format(when, 'p')}` : '—';
              const selectedStaff = assignments[apt.id] || '';
              return (
                <div
                  key={apt.id}
                  className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 rounded-lg border border-amber-400/15 bg-black/30 px-3 py-2.5"
                  data-testid={`website-booking-${apt.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-amber-50 truncate">
                      {p ? `${p.first_name} ${p.last_name}` : 'Unknown patient'}
                      <span className="ml-2 text-xs font-normal text-amber-200/70">{svc?.name || 'Service'}</span>
                    </p>
                    <p className="text-xs text-amber-100/70 mt-0.5">
                      {whenLabel}
                      {p?.phone && <span className="ml-2 text-amber-200/60">· {p.phone}</span>}
                      {p?.email && <span className="ml-2 text-amber-200/60">· {p.email}</span>}
                    </p>
                  </div>

                  {/* Nurse picker + actions */}
                  <div className="flex items-center gap-2 shrink-0">
                    {activeStaff.length > 0 ? (
                      <Select
                        value={selectedStaff}
                        onValueChange={(v) => setAssignments(prev => ({ ...prev, [apt.id]: v }))}
                      >
                        <SelectTrigger
                          className="h-8 min-w-[150px] max-w-[200px] text-xs border-amber-400/30 bg-black/40 text-amber-100"
                          data-testid={`assign-nurse-${apt.id}`}
                        >
                          <SelectValue placeholder="Assign nurse…" />
                        </SelectTrigger>
                        <SelectContent>
                          {activeStaff.map(s => {
                            const displayName = s.name || `${s.first_name || ''} ${s.last_name || ''}`.trim() || s.email || 'Staff';
                            return (
                              <SelectItem key={s.id} value={s.id} data-testid={`nurse-option-${s.id}`}>
                                {displayName}{s.role ? ` · ${s.role}` : ''}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span
                        className="text-[10px] italic text-amber-200/60 hidden sm:inline-block max-w-[160px] leading-tight"
                        data-testid={`no-nurses-hint-${apt.id}`}
                        title="Add nurses under the Staff tab and they'll appear here for future bookings"
                      >
                        No nurses on staff yet — add one under the <strong className="text-amber-300 not-italic">Staff</strong> tab
                      </span>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-amber-400/40 text-amber-200 hover:bg-amber-400/10 h-8 text-xs"
                      onClick={() => onJumpToDate && onJumpToDate(apt.start_datetime)}
                      data-testid={`view-website-booking-${apt.id}`}
                    >
                      View on calendar
                    </Button>
                    <Button
                      size="sm"
                      className="h-8 text-xs bg-amber-400 text-black hover:bg-amber-300"
                      disabled={busyId === apt.id}
                      onClick={() => confirmOne(apt)}
                      data-testid={`confirm-website-booking-${apt.id}`}
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                      {busyId === apt.id ? 'Confirming…' : 'Confirm'}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {pending.length > 3 && (
            <button
              type="button"
              className="mt-2 text-xs text-amber-300 hover:text-amber-200 underline underline-offset-2"
              onClick={() => setExpanded(v => !v)}
              data-testid="website-bookings-toggle"
            >
              {expanded ? 'Show less' : `Show ${pending.length - 3} more`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== BOOKING REJECTIONS BANNER ====================
// Surfaces website booking attempts that FAILED to resolve to a valid service.
// Gives admin the phone/email/service-name so they can call the patient back
// and book them manually, then dismiss the row.
export const BookingRejectionsBanner = ({ rejections, onRefresh }) => {
  const [busyId, setBusyId] = useState(null);
  const [expanded, setExpanded] = useState(false);

  if (!rejections || rejections.length === 0) return null;

  const dismiss = async (id) => {
    try {
      setBusyId(id);
      await axios.post(`${API}/booking-rejections/${id}/dismiss`);
      toast.success('Dismissed');
      onRefresh && onRefresh();
    } catch (e) {
      toast.error('Failed to dismiss');
    } finally {
      setBusyId(null);
    }
  };

  const rows = expanded ? rejections : rejections.slice(0, 3);

  return (
    <div
      className="mb-6 rounded-xl border border-rose-500/40 bg-gradient-to-r from-rose-500/10 via-rose-400/[0.06] to-transparent p-4 sm:p-5 shadow-[0_0_30px_rgba(244,63,94,0.08)]"
      data-testid="booking-rejections-banner"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 shrink-0 rounded-full bg-rose-500/15 p-2 ring-1 ring-rose-500/40">
          <XCircle className="w-4 h-4 text-rose-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.22em] uppercase text-rose-300">
              <AlertTriangle className="w-3 h-3" /> Booking Attempts That Failed
            </span>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-200 text-[11px] font-semibold ring-1 ring-rose-500/30" data-testid="booking-rejections-count">
              {rejections.length} in last 24h
            </span>
          </div>
          <p className="mt-1 text-sm text-rose-100/90">
            {rejections.length === 1 ? 'A patient tried to book on the website' : `${rejections.length} patients tried to book on the website`} but the service name they sent didn&apos;t match your EMR catalog. Call them back and book manually.
          </p>

          <div className="mt-3 space-y-2">
            {rows.map(r => {
              const when = r.created_at ? new Date(r.created_at) : null;
              const whenLabel = when ? format(when, 'MMM d · h:mm a') : '—';
              const requested = r.requested_datetime ? new Date(r.requested_datetime) : null;
              const requestedLabel = requested ? format(requested, 'EEE, MMM d · h:mm a') : '—';
              return (
                <div
                  key={r.id}
                  className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 rounded-lg border border-rose-500/20 bg-black/30 px-3 py-2.5"
                  data-testid={`booking-rejection-${r.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-rose-50 truncate">
                      {`${r.first_name || ''} ${r.last_name || ''}`.trim() || 'Unknown patient'}
                      <span className="ml-2 text-[10px] font-normal text-rose-200/60 uppercase tracking-wider">
                        · attempted {whenLabel}
                      </span>
                    </p>
                    <p className="text-xs text-rose-100/70 mt-0.5">
                      {r.phone && (
                        <a href={`tel:${r.phone}`} className="inline-flex items-center gap-1 text-rose-200 hover:text-rose-100 mr-2" data-testid={`rejection-call-${r.id}`}>
                          <PhoneCall className="w-3 h-3" /> {r.phone}
                        </a>
                      )}
                      {r.email && (
                        <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1 text-rose-200 hover:text-rose-100 mr-2" data-testid={`rejection-mail-${r.id}`}>
                          <Mail className="w-3 h-3" /> {r.email}
                        </a>
                      )}
                    </p>
                    <p className="text-[11px] text-rose-100/60 mt-1">
                      Wanted: <strong className="text-rose-200">{r.attempted_service_name || r.attempted_service_id || '(unknown service)'}</strong>
                      <span className="mx-1">·</span>
                      for <strong className="text-rose-200">{requestedLabel}</strong>
                    </p>
                    {r.notes && <p className="text-[11px] text-rose-100/50 italic mt-0.5 truncate">&ldquo;{r.notes}&rdquo;</p>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 text-xs text-rose-200 hover:bg-rose-500/10"
                      disabled={busyId === r.id}
                      onClick={() => dismiss(r.id)}
                      data-testid={`rejection-dismiss-${r.id}`}
                    >
                      {busyId === r.id ? 'Dismissing…' : 'Dismiss'}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {rejections.length > 3 && (
            <button
              type="button"
              className="mt-2 text-xs text-rose-300 hover:text-rose-200 underline underline-offset-2"
              onClick={() => setExpanded(v => !v)}
              data-testid="booking-rejections-toggle"
            >
              {expanded ? 'Show less' : `Show ${rejections.length - 3} more`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== MAIN EMR PAGE ====================
